<template>
  <div class="card">
    <div class="card-header border-0 pt-5">
      <h3 class="card-title align-items-start flex-column">
        <span class="card-label fw-bold fs-3 mb-1">
          Statistik Burung Walet
          <span class="badge badge-light-primary fs-8 align-middle ms-2">{{
            statsVersion === "v2" ? "MW IoT v2 · Tuya" : "MW IoT v1 · Lama"
          }}</span>
        </span>
        <span class="text-muted mt-1 fw-semobold fs-7"
          >{{
            statsVersion === "v2"
              ? "Kamera Tuya Bardi A50"
              : "Perangkat generasi lama (non-Tuya)"
          }}
          — {{ deviceCount }} perangkat</span
        >
      </h3>
      <div class="card-toolbar">
        <button @click="switchVersion" class="btn btn-sm btn-light me-2">
          <i class="bi bi-arrow-left-right me-1"></i>
          {{ statsVersion === "v2" ? "Lihat IoT v1" : "Lihat IoT v2" }}
        </button>
        <button @click="refreshData" class="btn btn-sm btn-light-primary me-2">
          <span class="svg-icon svg-icon-2">
            <inline-svg src="/media/icons/duotune/arrows/arr075.svg" />
          </span>
          Refresh Data
        </button>
      </div>
    </div>

    <!-- Filter Section -->
    <div class="card-body pb-0">
      <div class="row mb-5">
        <div class="col-md-3 mb-2">
          <select
            v-model="filterProvince"
            class="form-select"
            @change="applyFilters"
          >
            <option value="">Semua Provinsi</option>
            <option
              v-for="province in provinces"
              :key="province"
              :value="province"
            >
              {{ province }}
            </option>
          </select>
        </div>
        <div class="col-md-3 mb-2">
          <select
            v-model="filterCity"
            class="form-select"
            @change="applyFilters"
          >
            <option value="">Semua Kota</option>
            <option v-for="city in filteredCities" :key="city" :value="city">
              {{ city }}
            </option>
          </select>
        </div>
        <div class="col-md-3 mb-2">
          <select
            v-model="timeRange"
            class="form-select"
            @change="applyFilters"
          >
            <option value="day">Hari Ini</option>
            <option value="week">Minggu Ini</option>
            <option value="month">Bulan Ini</option>
            <option value="year">Tahun Ini</option>
          </select>
        </div>
        <div class="col-md-3 mb-2">
          <select v-model="selectedDeviceId" class="form-select">
            <option value="all">Semua Perangkat</option>
            <option
              v-for="d in filteredDeviceData"
              :key="d.device_id"
              :value="String(d.device_id)"
            >
              {{ d.name }}
            </option>
          </select>
        </div>
        <div class="col-md-3 mb-2">
          <div class="input-group">
            <input
              type="search"
              class="form-control"
              placeholder="Cari lokasi..."
              v-model="searchQuery"
              @input="applyFilters"
              name="mw-stats-search"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
              readonly
              @focus="$event.target.removeAttribute('readonly')"
            />
            <span class="input-group-text">
              <i class="bi bi-search"></i>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Summary Cards -->
    <div class="card-body pt-0">
      <div
        v-if="selectedDeviceId !== 'all'"
        class="alert alert-primary d-flex align-items-center py-3 mb-5"
      >
        <i class="bi bi-graph-up-arrow fs-3 me-3"></i>
        <span class="flex-grow-1"
          >Menampilkan statistik untuk perangkat:
          <b>{{ scopedDeviceName }}</b></span
        >
        <button
          class="btn btn-sm btn-light"
          @click="selectedDeviceId = 'all'"
        >
          <i class="bi bi-x-lg me-1"></i>Semua Perangkat
        </button>
      </div>
      <div class="row g-5 g-xl-8 mb-5">
        <div class="col-xl col-md-6">
          <div class="card bg-light-primary card-xl-stretch mb-xl-8">
            <div class="card-body my-3">
              <div class="d-flex align-items-center">
                <div class="symbol symbol-40px me-4">
                  <span class="symbol-label bg-primary">
                    <span class="svg-icon svg-icon-1 svg-icon-white">
                      <inline-svg
                        src="/media/icons/duotune/general/gen032.svg"
                      />
                    </span>
                  </span>
                </div>
                <div>
                  <div class="fs-2 fw-bold text-gray-800">
                    {{ formatNumber(kpiCrossings) }}
                  </div>
                  <div class="fs-7 text-gray-600">
                    Total Lintasan Walet ({{ timeRangeLabel }})
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl col-md-6">
          <div class="card bg-light-info card-xl-stretch mb-xl-8">
            <div class="card-body my-3">
              <div class="d-flex align-items-center">
                <div class="symbol symbol-40px me-4">
                  <span class="symbol-label bg-info">
                    <span class="svg-icon svg-icon-1 svg-icon-white">
                      <inline-svg
                        src="/media/icons/duotune/general/gen060.svg"
                      />
                    </span>
                  </span>
                </div>
                <div>
                  <div class="fs-2 fw-bold text-gray-800">
                    {{ formatNumber(kpiIn) }}
                    <span class="fs-5 text-gray-500"
                      >/ {{ formatNumber(kpiOut) }}</span
                    >
                  </div>
                  <div class="fs-7 text-gray-600">Masuk / Keluar</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl col-md-6">
          <div class="card bg-light-success card-xl-stretch mb-xl-8">
            <div class="card-body my-3">
              <div class="d-flex align-items-center">
                <div class="symbol symbol-40px me-4">
                  <span class="symbol-label bg-success">
                    <span class="svg-icon svg-icon-1 svg-icon-white">
                      <inline-svg
                        src="/media/icons/duotune/arrows/arr066.svg"
                      />
                    </span>
                  </span>
                </div>
                <div>
                  <div class="fs-2 fw-bold text-gray-800">
                    {{ growthRate >= 0 ? "+" : "" }}{{ growthRate.toFixed(1) }}%
                  </div>
                  <div class="fs-7 text-gray-600">
                    Pertumbuhan {{ timeRangeLabel }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl col-md-6">
          <div class="card bg-light-warning card-xl-stretch mb-xl-8">
            <div class="card-body my-3">
              <div class="d-flex align-items-center">
                <div class="symbol symbol-40px me-4">
                  <span class="symbol-label bg-warning">
                    <span class="svg-icon svg-icon-1 svg-icon-white">
                      <inline-svg
                        src="/media/icons/duotune/general/gen007.svg"
                      />
                    </span>
                  </span>
                </div>
                <div>
                  <div class="fs-2 fw-bold text-gray-800">
                    {{ peakLabel }}
                  </div>
                  <div class="fs-7 text-gray-600">Jam Puncak (WIB)</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-xl col-md-6">
          <div class="card bg-light-danger card-xl-stretch mb-xl-8">
            <div class="card-body my-3">
              <div class="d-flex align-items-center">
                <div class="symbol symbol-40px me-4">
                  <span class="symbol-label bg-danger">
                    <span class="svg-icon svg-icon-1 svg-icon-white">
                      <inline-svg
                        src="/media/icons/duotune/general/gen013.svg"
                      />
                    </span>
                  </span>
                </div>
                <div>
                  <div class="fs-2 fw-bold text-gray-800">
                    {{ topLocationLabel }}
                  </div>
                  <div class="fs-7 text-gray-600">Lokasi Terpadat</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Charts -->
      <div class="row g-5 g-xl-8 mb-5">
        <div class="col-xl-8">
          <div class="card card-xl-stretch mb-xl-8">
            <div class="card-header border-0 pt-5">
              <h3 class="card-title align-items-start flex-column">
                <span class="card-label fw-bold fs-3 mb-1"
                  >Tren Populasi Burung Walet</span
                >
                <span class="text-muted mt-1 fw-semobold fs-7">{{
                  timeRangeLabel
                }}</span>
              </h3>
              <div class="card-toolbar">
                <button
                  type="button"
                  class="btn btn-sm btn-icon btn-color-primary btn-active-light-primary"
                >
                  <span class="svg-icon svg-icon-2">
                    <inline-svg src="/media/icons/duotune/general/gen024.svg" />
                  </span>
                </button>
              </div>
            </div>
            <div class="card-body">
              <div ref="populationTrendChart" style="height: 350px"></div>
            </div>
          </div>
        </div>
        <div class="col-xl-4">
          <div class="card card-xl-stretch mb-xl-8">
            <div class="card-header border-0 pt-5">
              <h3 class="card-title align-items-start flex-column">
                <span class="card-label fw-bold fs-3 mb-1"
                  >Distribusi Lokasi</span
                >
                <span class="text-muted mt-1 fw-semobold fs-7"
                  >Jumlah Burung per Area</span
                >
              </h3>
              <div class="card-toolbar">
                <button
                  type="button"
                  class="btn btn-sm btn-icon btn-color-primary btn-active-light-primary"
                >
                  <span class="svg-icon svg-icon-2">
                    <inline-svg src="/media/icons/duotune/general/gen024.svg" />
                  </span>
                </button>
              </div>
            </div>
            <div class="card-body">
              <div ref="locationDistributionChart" style="height: 350px"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Additional Charts -->
      <div class="row g-5 g-xl-8 mb-5">
        <div class="col-xl-6">
          <div class="card card-xl-stretch mb-xl-8">
            <div class="card-header border-0 pt-5">
              <h3 class="card-title align-items-start flex-column">
                <span class="card-label fw-bold fs-3 mb-1"
                  >Tingkat Aktivitas</span
                >
                <span class="text-muted mt-1 fw-semobold fs-7"
                  >Berdasarkan Waktu Hari</span
                >
              </h3>
            </div>
            <div class="card-body">
              <div ref="activityLevelChart" style="height: 300px"></div>
            </div>
          </div>
        </div>
        <div class="col-xl-6">
          <div class="card card-xl-stretch mb-xl-8">
            <div class="card-header border-0 pt-5">
              <h3 class="card-title align-items-start flex-column">
                <span class="card-label fw-bold fs-3 mb-1"
                  >Perbandingan Provinsi</span
                >
                <span class="text-muted mt-1 fw-semobold fs-7"
                  >Total Populasi Burung Walet</span
                >
              </h3>
            </div>
            <div class="card-body">
              <div ref="provinceComparisonChart" style="height: 300px"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Data Table -->
      <div class="card card-xl-stretch mb-5">
        <div class="card-header border-0 pt-5">
          <h3 class="card-title align-items-start flex-column">
            <span class="card-label fw-bold fs-3 mb-1"
              >Detail Data Perperangkat</span
            >
            <span class="text-muted mt-1 fw-semobold fs-7"
              >{{ filteredDeviceData.length }} Perangkat Terdaftar</span
            >
          </h3>
          <div class="card-toolbar">
            <button class="btn btn-sm btn-light-primary" @click="exportToCSV">
              <span class="svg-icon svg-icon-2">
                <inline-svg src="/media/icons/duotune/files/fil007.svg" />
              </span>
              Export CSV
            </button>
          </div>
        </div>
        <div class="card-body py-3">
          <div class="table-responsive">
            <table class="table align-middle gs-0 gy-4">
              <thead>
                <tr class="fw-bold text-muted bg-light">
                  <th class="ps-4 min-w-100px rounded-start">ID Perangkat</th>
                  <th class="min-w-125px">Lokasi</th>
                  <th class="min-w-125px">Provinsi</th>
                  <th class="min-w-100px">Kota</th>
                  <th class="min-w-100px">Total Lintasan</th>
                  <th class="min-w-125px">Terakhir Update</th>
                  <th class="min-w-200px">Status</th>
                  <th class="min-w-150px rounded-end">Aksi</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="device in paginatedDeviceData" :key="device.id">
                  <td class="ps-4">
                    <span class="text-dark fw-semibold d-block mb-1 fs-6">{{
                      device.id
                    }}</span>
                  </td>
                  <td>
                    <span class="text-dark fw-semibold d-block mb-1 fs-6">{{
                      device.name
                    }}</span>
                  </td>
                  <td>
                    <span class="text-dark fw-semibold d-block mb-1 fs-6">{{
                      device.province
                    }}</span>
                  </td>
                  <td>
                    <span class="text-dark fw-semibold d-block mb-1 fs-6">{{
                      device.city
                    }}</span>
                  </td>
                  <td>
                    <span class="text-dark fw-semibold d-block mb-1 fs-6">{{
                      device.total_birds
                    }}</span>
                  </td>
                  <td>
                    <span class="text-dark fw-semibold d-block mb-1 fs-6">{{
                      formatDate(device.last_updated)
                    }}</span>
                  </td>
                  <td>
                    <span
                      :class="`badge badge-light-${
                        device.online ? 'success' : 'danger'
                      } fw-semibold`"
                    >
                      {{ device.online ? "Online" : "Offline" }}
                    </span>
                  </td>
                  <td>
                    <div class="d-flex align-items-center gap-2">
                      <button
                        class="btn btn-sm btn-primary"
                        @click="viewDeviceStats(device.device_id)"
                        title="Lihat statistik lengkap perangkat ini"
                      >
                        <i class="bi bi-graph-up-arrow me-1"></i>Statistik
                      </button>
                      <button
                        class="btn btn-sm btn-icon btn-light"
                        @click="viewDeviceDetails(device.id)"
                        title="Kelola perangkat (administratif)"
                      >
                        <i class="bi bi-gear"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div
            class="d-flex justify-content-between align-items-center flex-wrap mt-5"
          >
            <div class="d-flex flex-wrap py-2 mr-3">
              <select
                v-model="pageSize"
                class="form-select form-select-sm"
                style="width: 75px"
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
            </div>
            <div class="d-flex flex-wrap py-2">
              <ul class="pagination">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="changePage(currentPage - 1)"
                  >
                    <span class="previous"></span>
                  </a>
                </li>
                <li
                  v-for="page in totalPages"
                  :key="page"
                  class="page-item"
                  :class="{ active: page === currentPage }"
                >
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="changePage(page)"
                    >{{ page }}</a
                  >
                </li>
                <li
                  class="page-item"
                  :class="{ disabled: currentPage === totalPages }"
                >
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="changePage(currentPage + 1)"
                  >
                    <span class="next"></span>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import * as echarts from "echarts";
import { deviceApiService } from "@/services/DeviceApiService";

interface ChartDataPoint {
  value: number;
  name: string;
}

interface HistoricalDataPoint {
  date: Date | string;
  value: number;
}

interface ActivityData {
  hours: number[];
  values: number[];
}

interface SeriesPoint {
  label: string;
  in: number;
  out: number;
}
interface HourlyPoint {
  hour: number;
  in: number;
  out: number;
}
interface DeviceData {
  id: string;
  device_id: number;
  name: string;
  province: string;
  city: string;
  total_birds: number;
  total_in: number;
  total_out: number;
  total_crossings: number;
  peak_hour: number | null;
  live_inside: number;
  last_updated: Date;
  online: boolean;
  historical_data: HistoricalDataPoint[];
  activity_data: ActivityData;
  series: SeriesPoint[];
  hourly: HourlyPoint[];
}

export default defineComponent({
  name: "statistics-iot",
  setup() {
    const router = useRouter();
    const route = useRoute();
    // v2 = Tuya (Bardi A50) cameras; v1 = older non-Tuya units. Driven by route.
    const statsVersion = computed<"v1" | "v2">(() =>
      route.path.includes("/v1") ? "v1" : "v2"
    );
    const switchVersion = () => {
      router.push(
        statsVersion.value === "v2" ? "/iot/v1/statistics" : "/iot/statistics"
      );
    };
    // References for charts
    const populationTrendChart = ref<null | HTMLElement>(null);
    const locationDistributionChart = ref<null | HTMLElement>(null);
    const activityLevelChart = ref<null | HTMLElement>(null);
    const provinceComparisonChart = ref<null | HTMLElement>(null);

    // Chart instances
    let trendChart: echarts.ECharts | null = null;
    let distributionChart: echarts.ECharts | null = null;
    let activityChart: echarts.ECharts | null = null;
    let provinceChart: echarts.ECharts | null = null;

    // State variables
    const deviceData = ref<DeviceData[]>([]);
    const loading = ref(true);
    const filterProvince = ref("");
    const filterCity = ref("");
    const searchQuery = ref("");
    const timeRange = ref("day");
    const currentPage = ref(1);
    const pageSize = ref(10);

    // Statistics data
    const totalBirds = ref(0);
    const growthRate = ref(0);
    const avgBirdsPerNest = ref(0);
    const topLocation = ref("");
    const deviceCount = ref(0);
    // New analytics state (from the redesigned /bird-stats summary)
    const totalCrossings = ref(0);
    const totalIn = ref(0);
    const totalOut = ref(0);
    const activeDevices = ref(0);
    const peakHour = ref<number | null>(null);
    const summarySeries = ref<
      { label: string; in: number; out: number; crossings: number }[]
    >([]);
    // Per-device drill-down: "all" or a device_id (as string)
    const selectedDeviceId = ref<string>("all");

    // Derived data
    const provinces = computed(() => {
      const uniqueProvinces = new Set<string>();
      deviceData.value.forEach((device) => {
        if (device.province) uniqueProvinces.add(device.province);
      });
      return Array.from(uniqueProvinces).sort();
    });

    const cities = computed(() => {
      const uniqueCities = new Set<string>();
      deviceData.value.forEach((device) => {
        if (device.city) uniqueCities.add(device.city);
      });
      return Array.from(uniqueCities).sort();
    });

    const filteredCities = computed(() => {
      if (!filterProvince.value) return cities.value;
      const citiesInProvince = new Set<string>();
      deviceData.value
        .filter((device) => device.province === filterProvince.value)
        .forEach((device) => {
          if (device.city) citiesInProvince.add(device.city);
        });
      return Array.from(citiesInProvince).sort();
    });

    const filteredDeviceData = computed(() => {
      return deviceData.value
        .filter((device) => {
          const matchesProvince =
            !filterProvince.value || device.province === filterProvince.value;
          const matchesCity =
            !filterCity.value || device.city === filterCity.value;
          const matchesSearch =
            !searchQuery.value ||
            device.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
            device.city.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
            device.province
              .toLowerCase()
              .includes(searchQuery.value.toLowerCase());
          return matchesProvince && matchesCity && matchesSearch;
        })
        .sort((a, b) => b.total_birds - a.total_birds);
    });

    const paginatedDeviceData = computed(() => {
      const startIdx = (currentPage.value - 1) * pageSize.value;
      const endIdx = startIdx + pageSize.value;
      return filteredDeviceData.value.slice(startIdx, endIdx);
    });

    const totalPages = computed(() => {
      return Math.ceil(filteredDeviceData.value.length / pageSize.value);
    });

    // Drill-down scope: a specific device, or all devices matching the filters.
    const scopedDevice = computed(() =>
      selectedDeviceId.value === "all"
        ? null
        : deviceData.value.find(
            (d) => String(d.device_id) === selectedDeviceId.value
          ) || null
    );
    const scopeDevices = computed(() =>
      scopedDevice.value ? [scopedDevice.value] : filteredDeviceData.value
    );
    const scopedDeviceName = computed(() => scopedDevice.value?.name || "");

    // KPI values — respect both the device drill-down and province/city filters.
    const kpiCrossings = computed(() =>
      scopeDevices.value.reduce((s, d) => s + d.total_crossings, 0)
    );
    const kpiIn = computed(() =>
      scopeDevices.value.reduce((s, d) => s + d.total_in, 0)
    );
    const kpiOut = computed(() =>
      scopeDevices.value.reduce((s, d) => s + d.total_out, 0)
    );
    const kpiActiveDevices = computed(
      () => scopeDevices.value.filter((d) => d.total_crossings > 0).length
    );

    // Hourly profile (summed across scope) + the peak activity hour.
    const scopedHourly = computed(() => {
      const agg = Array.from({ length: 24 }, (_, h) => ({
        hour: h,
        in: 0,
        out: 0,
      }));
      scopeDevices.value.forEach((d) =>
        (d.hourly || []).forEach((hp) => {
          if (agg[hp.hour]) {
            agg[hp.hour].in += hp.in;
            agg[hp.hour].out += hp.out;
          }
        })
      );
      return agg;
    });
    const kpiPeakHour = computed(() => {
      let mi: number | null = null;
      let mv = 0;
      scopedHourly.value.forEach((x) => {
        const c = x.in + x.out;
        if (c > mv) {
          mv = c;
          mi = x.hour;
        }
      });
      return mi;
    });
    const peakLabel = computed(() =>
      kpiPeakHour.value == null
        ? "—"
        : `${String(kpiPeakHour.value).padStart(2, "0")}:00`
    );

    // Busiest location within the current scope.
    const topLocationLabel = computed(() => {
      let name = "—";
      let count = 0;
      scopeDevices.value.forEach((d) => {
        if (d.total_crossings > count) {
          count = d.total_crossings;
          name = d.city || d.name;
        }
      });
      return name;
    });

    // Trend series for the current scope (sum device series bucket-by-bucket;
    // every device shares the same timeframe buckets so indices align).
    const scopedSeries = computed(() => {
      const devs = scopeDevices.value.filter((d) => d.series && d.series.length);
      if (!devs.length) return summarySeries.value;
      const labels = devs[0].series.map((s) => s.label);
      return labels.map((label, i) => {
        let inS = 0;
        let outS = 0;
        devs.forEach((d) => {
          const p = d.series[i];
          if (p) {
            inS += p.in;
            outS += p.out;
          }
        });
        return { label, in: inS, out: outS, crossings: inS + outS };
      });
    });

    const timeRangeLabel = computed(() => {
      switch (timeRange.value) {
        case "day":
          return "Hari Ini";
        case "week":
          return "Minggu Ini";
        case "month":
          return "Bulan Ini";
        case "year":
          return "Tahun Ini";
        default:
          return "";
      }
    });

    // Methods
    const fetchDeviceData = async () => {
      loading.value = true;
      try {
        const range = timeRange.value as "day" | "week" | "month" | "year";
        const response = await deviceApiService.getBirdStats(
          range,
          undefined,
          statsVersion.value
        );

        deviceData.value = response.data.map((d) => ({
          id: d.device_id.toString(),
          name: d.device_name || `Device #${d.device_id}`,
          province: d.province || "",
          city: d.city || d.location || "-",
          device_id: d.device_id,
          total_birds: d.total_crossings ?? d.total_in ?? 0, // total activity (in+out)
          total_in: d.total_in ?? 0,
          total_out: d.total_out ?? 0,
          total_crossings: d.total_crossings ?? d.total_in ?? 0,
          peak_hour: d.peak_hour ?? null,
          live_inside: d.crossing_count,
          last_updated: new Date(d.last_recorded_at || d.updated_at),
          online: d.status === "online",
          series: (d.series || []).map((s) => ({
            label: s.label,
            in: s.in,
            out: s.out,
          })),
          hourly: (d.hourly || []).map((h) => ({
            hour: h.hour,
            in: h.in,
            out: h.out,
          })),
          historical_data: (d.series || []).map((s) => ({
            date: s.label,
            value: s.in + s.out,
          })),
          activity_data: {
            hours: (d.hourly || []).map((h) => h.hour),
            values: (d.hourly || []).map((h) => h.in + h.out),
          },
        }));

        // Summary metrics from the redesigned /bird-stats endpoint
        const sm = response.summary;
        totalCrossings.value = sm.total_crossings ?? sm.total_birds ?? 0;
        totalIn.value = sm.total_in ?? 0;
        totalOut.value = sm.total_out ?? 0;
        activeDevices.value = sm.active_devices ?? 0;
        peakHour.value = sm.peak_hour ?? null;
        growthRate.value = sm.growth_pct ?? 0;
        summarySeries.value = (sm.series || []).map((s) => ({
          label: s.label,
          in: s.in,
          out: s.out,
          crossings: s.crossings ?? s.in + s.out,
        }));
        totalBirds.value = totalCrossings.value;
        topLocation.value = sm.top_location;
        deviceCount.value = sm.device_count;
      } catch (error) {
        console.error("Error fetching bird stats:", error);
        deviceData.value = [];
      } finally {
        loading.value = false;
        calculateStatistics();
        // Init on first load, update-only on subsequent refreshes
        if (!trendChart) {
          initCharts();
        } else {
          updateCharts();
        }
      }
    };

    const calculateStatistics = () => {
      // growthRate is set from the backend (proper period-over-period) in
      // fetchDeviceData. Here we only derive the average over ACTIVE devices in
      // the current scope so empty/idle devices don't dilute it.
      const active = scopeDevices.value.filter((d) => d.total_crossings > 0);
      const total = active.reduce((s, d) => s + d.total_crossings, 0);
      avgBirdsPerNest.value =
        active.length > 0 ? Math.round(total / active.length) : 0;
    };

    const getComparisonDate = (date: Date, range: string): Date => {
      const result = new Date(date);

      switch (range) {
        case "day":
          result.setDate(result.getDate() - 1);
          break;
        case "week":
          result.setDate(result.getDate() - 7);
          break;
        case "month":
          result.setMonth(result.getMonth() - 1);
          break;
        case "year":
          result.setFullYear(result.getFullYear() - 1);
          break;
      }

      return result;
    };

    const initCharts = () => {
      if (
        !populationTrendChart.value ||
        !locationDistributionChart.value ||
        !activityLevelChart.value ||
        !provinceComparisonChart.value
      ) return;

      // Reuse existing instances to avoid re-init on every data refresh
      trendChart = echarts.getInstanceByDom(populationTrendChart.value) ||
        echarts.init(populationTrendChart.value);
      distributionChart = echarts.getInstanceByDom(locationDistributionChart.value) ||
        echarts.init(locationDistributionChart.value);
      activityChart = echarts.getInstanceByDom(activityLevelChart.value) ||
        echarts.init(activityLevelChart.value);
      provinceChart = echarts.getInstanceByDom(provinceComparisonChart.value) ||
        echarts.init(provinceComparisonChart.value);

      updateCharts();

      window.addEventListener("resize", () => {
        trendChart?.resize();
        distributionChart?.resize();
        activityChart?.resize();
        provinceChart?.resize();
      });
    };

    const updateCharts = () => {
      updatePopulationTrendChart();
      updateLocationDistributionChart();
      updateActivityLevelChart();
      updateProvinceComparisonChart();
    };

    const updatePopulationTrendChart = () => {
      if (!trendChart) return;

      // Timeframe-aware buckets straight from the backend series
      // (day=per jam, week=per hari, month=per minggu, year=per bulan),
      // scoped to the selected device / filters.
      const s = scopedSeries.value;
      const labels = s.map((b) => b.label);
      const inData = s.map((b) => b.in);
      const outData = s.map((b) => b.out);
      const anyOut = outData.some((v) => v > 0);
      const grad = (c1: string, c2: string) =>
        new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: c1 },
          { offset: 1, color: c2 },
        ]);

      const option = {
        color: ["#009ef7", "#f1416c"],
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "cross", label: { backgroundColor: "#3f4254" } },
        },
        legend: { data: ["Masuk", "Keluar"], top: 0, right: 10, icon: "circle" },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          top: "16%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: labels,
          axisLabel: { color: "#a1a5b7", hideOverlap: true },
          axisLine: { lineStyle: { color: "#e4e6ef" } },
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          axisLabel: {
            color: "#a1a5b7",
            formatter: (v: number) => v.toLocaleString(),
          },
          splitLine: { lineStyle: { color: "#eff2f5" } },
        },
        series: [
          {
            name: "Masuk",
            type: "line",
            smooth: true,
            symbol: "circle",
            symbolSize: 7,
            lineStyle: { width: 3 },
            areaStyle: {
              color: grad("rgba(0,158,247,0.35)", "rgba(0,158,247,0.02)"),
            },
            data: inData,
          },
          {
            name: "Keluar",
            type: "line",
            smooth: true,
            symbol: "circle",
            symbolSize: 7,
            lineStyle: { width: 3, type: anyOut ? "solid" : "dashed" },
            areaStyle: {
              color: grad("rgba(241,65,108,0.25)", "rgba(241,65,108,0.02)"),
            },
            data: outData,
          },
        ],
      };

      trendChart.setOption(option, true);
    };

    const updateLocationDistributionChart = () => {
      if (!distributionChart) return;

      // Process data for the chart
      const data: ChartDataPoint[] = [];
      const cityData = new Map<string, number>();

      // Aggregate data by city
      filteredDeviceData.value.forEach((device) => {
        if (cityData.has(device.city)) {
          cityData.set(
            device.city,
            cityData.get(device.city)! + device.total_birds
          );
        } else {
          cityData.set(device.city, device.total_birds);
        }
      });

      // Sort by bird count (descending) and get top 10
      Array.from(cityData.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .forEach(([city, count]) => {
          data.push({ value: count, name: city });
        });

      // Set chart options
      const option = {
        tooltip: {
          trigger: "item",
          formatter: "{b}: {c} burung ({d}%)",
        },
        legend: {
          orient: "vertical",
          left: "left",
          top: "center",
        },
        series: [
          {
            name: "Distribusi Lokasi",
            type: "pie",
            radius: ["40%", "70%"],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: "#fff",
              borderWidth: 2,
            },
            label: {
              show: false,
              position: "center",
            },
            emphasis: {
              label: {
                show: true,
                fontSize: "14",
                fontWeight: "bold",
              },
            },
            labelLine: {
              show: false,
            },
            data: data,
          },
        ],
      };

      distributionChart.setOption(option);
    };

    const updateActivityLevelChart = () => {
      if (!activityChart) return;

      // Hourly profile (WIB) for the current scope: when are birds most active?
      const h = scopedHourly.value;
      const hours = h.map((x) => `${String(x.hour).padStart(2, "0")}:00`);
      const inData = h.map((x) => x.in);
      const outData = h.map((x) => x.out);
      const anyOut = outData.some((v) => v > 0);

      const series: any[] = [
        {
          name: "Masuk",
          type: "bar",
          stack: "act",
          data: inData,
          itemStyle: {
            color: "#009ef7",
            borderRadius: anyOut ? 0 : [4, 4, 0, 0],
          },
        },
      ];
      if (anyOut) {
        series.push({
          name: "Keluar",
          type: "bar",
          stack: "act",
          data: outData,
          itemStyle: { color: "#f1416c", borderRadius: [4, 4, 0, 0] },
        });
      }

      const option = {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        legend: {
          data: anyOut ? ["Masuk", "Keluar"] : ["Masuk"],
          top: 0,
          right: 10,
          icon: "circle",
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          top: "16%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: hours,
          axisLabel: { interval: 1, color: "#a1a5b7", fontSize: 10 },
          axisLine: { lineStyle: { color: "#e4e6ef" } },
        },
        yAxis: {
          type: "value",
          minInterval: 1,
          axisLabel: {
            color: "#a1a5b7",
            formatter: (v: number) => v.toLocaleString(),
          },
          splitLine: { lineStyle: { color: "#eff2f5" } },
        },
        series,
      };

      activityChart.setOption(option, true);
    };

    const updateProvinceComparisonChart = () => {
      if (!provinceChart) return;

      // Process data for the chart
      const provinceData = new Map<string, number>();

      // Aggregate data by province
      deviceData.value.forEach((device) => {
        if (provinceData.has(device.province)) {
          provinceData.set(
            device.province,
            provinceData.get(device.province)! + device.total_birds
          );
        } else {
          provinceData.set(device.province, device.total_birds);
        }
      });

      // Sort by bird count (descending)
      const sortedData = Array.from(provinceData.entries()).sort(
        (a, b) => b[1] - a[1]
      );

      const provinces = sortedData.map((item) => item[0]);
      const values = sortedData.map((item) => item[1]);

      // Set chart options
      const option = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: function (params) {
            return `${
              params[0].name
            }: ${params[0].value.toLocaleString()} burung`;
          },
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          containLabel: true,
        },
        xAxis: {
          type: "value",
          axisLabel: {
            formatter: function (value) {
              return value.toLocaleString();
            },
          },
        },
        yAxis: {
          type: "category",
          data: provinces,
          axisLabel: {
            interval: 0,
          },
        },
        series: [
          {
            name: "Total Burung",
            type: "bar",
            data: values,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(1, 0, 0, 0, [
                { offset: 0, color: "#ffb3b3" },
                { offset: 0.5, color: "#ff7373" },
                { offset: 1, color: "#ff0000" },
              ]),
            },
          },
        ],
      };

      provinceChart.setOption(option);
    };

    const applyFilters = () => {
      // Reset pagination
      currentPage.value = 1;

      // If a province is selected but the city is not in that province, reset city filter
      if (filterProvince.value && filterCity.value) {
        const citiesInSelectedProvince = deviceData.value
          .filter((device) => device.province === filterProvince.value)
          .map((device) => device.city);

        if (!citiesInSelectedProvince.includes(filterCity.value)) {
          filterCity.value = "";
        }
      }

      // Recalculate statistics and update charts
      calculateStatistics();
      updateCharts();
    };

    const refreshData = () => {
      fetchDeviceData();
    };

    const changePage = (page: number) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
      }
    };

    const formatNumber = (value: number): string => {
      return value.toLocaleString();
    };

    const formatDate = (date: Date): string => {
      if (!date) return "-";
      return new Date(date).toLocaleString("id-ID", {
        timeZone: "Asia/Jakarta",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    // Primary table action: drill into this device's analytics in-page.
    const viewDeviceStats = (deviceId: number) => {
      selectedDeviceId.value = String(deviceId);
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // Secondary action: administrative management on the devices page.
    const viewDeviceDetails = (id: string) => {
      router.push({ name: "iot-devices", query: { highlight: id } });
    };

    const editDevice = (id: string) => {
      router.push({ name: "iot-devices", query: { edit: id } });
    };

    const exportToCSV = () => {
      const data = filteredDeviceData.value;

      // Prepare CSV content
      const headers = [
        "ID",
        "Nama",
        "Provinsi",
        "Kota",
        "Total Burung",
        "Terakhir Update",
        "Status",
      ];
      const csvContent = [
        headers.join(","),
        ...data.map((device) =>
          [
            device.id,
            `"${device.name}"`, // Add quotes to handle commas in names
            device.province,
            device.city,
            device.total_birds,
            new Date(device.last_updated).toLocaleString(),
            device.online ? "Online" : "Offline",
          ].join(",")
        ),
      ].join("\n");

      // Create download link
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `statistik-walet-${new Date().toISOString().slice(0, 10)}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    // Watch for changes in time range - refetch real data
    watch(timeRange, () => {
      fetchDeviceData();
    });

    // Switching between v1/v2 pages (same component) re-scopes + refetches.
    watch(statsVersion, () => {
      selectedDeviceId.value = "all";
      currentPage.value = 1;
      fetchDeviceData();
    });

    // Device drill-down re-scopes KPIs + charts from already-loaded data.
    watch(selectedDeviceId, () => {
      calculateStatistics();
      updateCharts();
    });

    // Auto-refresh every 60 seconds for realtime data
    let _refreshInterval: ReturnType<typeof setInterval> | null = null;

    onMounted(() => {
      setCurrentPageBreadcrumbs("Statistik IoT", ["Dashboard"]);
      fetchDeviceData();
      _refreshInterval = setInterval(() => {
        fetchDeviceData();
      }, 60000);
    });

    onUnmounted(() => {
      if (_refreshInterval) clearInterval(_refreshInterval);
    });

    return {
      // References
      populationTrendChart,
      locationDistributionChart,
      activityLevelChart,
      provinceComparisonChart,

      // State
      deviceData,
      loading,
      filterProvince,
      filterCity,
      searchQuery,
      timeRange,
      currentPage,
      pageSize,

      // Computed
      provinces,
      cities,
      filteredCities,
      filteredDeviceData,
      paginatedDeviceData,
      totalPages,
      timeRangeLabel,

      // Statistics
      totalBirds,
      growthRate,
      avgBirdsPerNest,
      topLocation,
      deviceCount,
      // New analytics
      statsVersion,
      switchVersion,
      selectedDeviceId,
      scopedDeviceName,
      kpiCrossings,
      kpiIn,
      kpiOut,
      kpiActiveDevices,
      activeDevices,
      peakLabel,
      topLocationLabel,
      viewDeviceStats,

      // Methods
      applyFilters,
      refreshData,
      changePage,
      formatNumber,
      formatDate,
      viewDeviceDetails,
      editDevice,
      exportToCSV,
    };
  },
});
</script>
